/**
 * Project Detail Page
 *
 * Displays comprehensive project information with tabs for:
 * - Overview: Budget summary and recent activity
 * - Quotations: Related quotations
 * - Expenses: Project expenses
 * - Progress: Progress photos and timeline
 * - Files: Project files
 *
 * @module pages/projects/ProjectDetail
 */

import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Tab,
  Tabs,
  Spinner,
  Alert,
  Modal,
  Form,
  Table,
  Dropdown,
} from 'react-bootstrap';
import { toast } from '../../utils/toast';
import {
  getProject,
  updateProject,
} from '../../services/projectService';
import {
  approveQuotation,
  rejectQuotation,
} from '../../services/quotationService';
import { getProjectExpenses } from '../../services/expenseService';
import { getProgressPhotos } from '../../services/progressService';
import { listTasks } from '../../services/taskService';
import {
  formatProjectStatus,
  formatQuotationStatus,
  formatCurrency,
  formatDate,
  formatPercentage,
} from '../../utils/formatters';
import TaskStatusBadge from '../../components/tasks/shared/TaskStatusBadge';
import TaskPriorityBadge from '../../components/tasks/shared/TaskPriorityBadge';
import { PROJECT_STATUS, QUOTATION_STATUS } from '../../utils/constants';
// #765 — los umbrales 80/100, nombrados una sola vez en el árbol web.
import { BUDGET_WARNING_THRESHOLD, BUDGET_CRITICAL_THRESHOLD } from '../../utils/budgetStatus';
import { useAuth } from '../../contexts/AuthContext';
import BudgetAlertBanner from '../../components/common/BudgetAlertBanner';
import KebabMenu from '../../components/common/KebabMenu';
import ProjectClosureWizard from '../../components/projects/ProjectClosureWizard';
import { getClosure } from '../../services/closureService';
import { ProjectTimeline } from '../../components/timeline';
import DashboardLayout from '../../components/layout/DashboardLayout';
import MilestoneList from '../../components/milestones/MilestoneList';
import InvoiceList from '../../components/invoices/InvoiceList';
import ProjectLeadersPanel from '../../components/projects/ProjectLeadersPanel';
import ProjectMaterialsTab from '../../components/projects/ProjectMaterialsTab';
import './ProjectDetail.css';

/**
 * ProjectDetail component
 */
const ProjectDetail = () => {
  const navigate = useNavigate();
  const { id: projectId } = useParams();
  const queryClient = useQueryClient();
  const { isAdmin, isSupervisor, isTechnician, isClient } = useAuth();
  // Era `isAdmin || isSupervisor`, y para el supervisor el botón estaba
  // GARANTIZADO a fallar: en `projects` sólo tiene `supervisor_view_projects
  // (SELECT)` — verificado por SELECT contra producción. Apretaba "Editar
  // Presupuesto", guardaba, y recibía "Error al actualizar el presupuesto" sin
  // ninguna explicación.
  //
  // Se quita la afordancia en vez de conceder el permiso, y no es un descuido:
  // la matriz dice "Editar proyecto: Supervisor ❌", y lo que Jorge decidió es
  // que el supervisor VEA presupuesto y gastado —para decidir compras—, no que
  // los fije. Conceder un UPDATE aquí sería inventar una regla que nadie tomó.
  //
  // Si mañana se decide que sí, no alcanza con volver a poner `isSupervisor`:
  // hace falta una policy, y como el presupuesto es una columna suelta dentro
  // de `projects`, lo correcto sería una función SECURITY DEFINER que sólo
  // toque ese campo — un UPDATE amplio le abriría la tabla entera.
  const canEditBudget = isAdmin;

  // #500 — internal-cost gate.
  //
  // A client reaching this page is DELIBERATE (see navConfig.js: "a client's own
  // project dates/payments already surface on their project detail page"). What
  // was never reviewed is WHAT the page shows once they get here: "Total
  // Gastado" is ProMedic's cost, "Disponible" is quoted - spent = ProMedic's
  // MARGIN, and "Uso del Presupuesto" is that same margin as a percentage.
  //
  // Allowlist of internal roles, deliberately NOT `!isClient`: if `profile`
  // hasn't loaded yet, or a new role gets added later, the safe default is to
  // show nothing internal. Failing closed here costs a staff user one render;
  // failing open costs the company its margin on every project.
  //
  // #518 — the TECHNICIAN was removed. Jorge split two things this page had
  // fused: the internal cost of the project (spent / available / margin) is
  // admin+supervisor, while the purchase price of a material is a separate,
  // deliberately wider concept that this gate does not touch.
  //
  // This does not take away anything the technician works from: their own
  // portal page never showed financials by design
  // (technician/TechnicianProjectDetail.js) and every `/gastos` route is
  // already `RoleGuard allowedRoles={['admin']}` (App.js:301-305), so they
  // have never recorded or listed an expense from the web at all.
  const canViewInternalCosts = isAdmin || isSupervisor;

  // #518 — "Monto Cotizado" is a SEPARATE gate, not a looser copy of the one
  // above, and the two must not be merged:
  //
  //   admin ✅   supervisor ✅   technician ❌   client ✅
  //
  // The CLIENT sees it because it is their own price — the number they agreed
  // to pay. The TECHNICIAN does not, because Jorge decided what we charge the
  // client is not theirs to see. Folding this into `canViewInternalCosts`
  // would either strip the client of their own price (a bug) or hand it back
  // to the technician; there is no single flag that does both.
  //
  // The SUPERVISOR keeps it for a structural reason, not a preference:
  // `projects` has exactly ONE money column, `quoted_amount`, and it doubles
  // as the client price AND the spending budget ("Disponible" is
  // quoted_amount − spent). Jorge wants the supervisor to see the budget, and
  // today that is physically the same field. Splitting price from budget is
  // tracked in #519 — until it lands, this is not an oversight.
  //
  // Allowlist, same as above: an unloaded profile or a future role (Operario,
  // Mensajero — #463) shows nothing.
  const canViewQuotedAmount = isAdmin || isSupervisor || isClient;

  // Validación de inventario (pestaña Materiales). El gate espeja EXACTAMENTE
  // los `roles` de la tool `check_materials_availability` en mcpDiscovery.js
  // (admin/supervisor/technician), que es la misma capacidad por el otro carril:
  // si la web fuera más ancha que la tool, el mismo usuario vería por pantalla
  // algo que el agente le niega, y al revés sería la pestaña que se abre para
  // dar un 403.
  //
  // El TÉCNICO entra a propósito: es quien está en obra preguntándose qué hay
  // en bodega, y esta pantalla no muestra un solo precio — es cantidades y
  // ubicaciones, no costo interno (por eso no cuelga de `canViewInternalCosts`,
  // que es admin+supervisor desde #518).
  //
  // El CLIENTE no: el stock de ProMedic y lo que le falta comprar para hacer
  // este trabajo es información interna, igual que Gastos (#500).
  const canValidateMaterials = isAdmin || isSupervisor || isTechnician;

  const [activeTab, setActiveTab] = useState('overview');
  const [highlightedEventId, setHighlightedEventId] = useState(null);
  const [showClosureWizard, setShowClosureWizard] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [isEditingPO, setIsEditingPO] = useState(false);
  const [editedPONumber, setEditedPONumber] = useState('');
  const [isEditingBudget, setIsEditingBudget] = useState(false);
  const [editedBudget, setEditedBudget] = useState('');

  // Quotation approval/rejection state
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [quotationActionLoading, setQuotationActionLoading] = useState(false);

  // Fetch project data
  const {
    data: projectData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => getProject(projectId),
    enabled: !!projectId,
  });

  // Fetch project expenses
  const {
    data: expensesData,
  } = useQuery({
    queryKey: ['projectExpenses', projectId],
    queryFn: () => getProjectExpenses(projectId),
    // #500: never issue the expenses read at all for a client. The tab is gone
    // for them below, but a query that only *happens* not to fire is not a
    // control — this makes the client lane explicit.
    enabled: !!projectId && activeTab === 'expenses' && canViewInternalCosts,
  });

  // Fetch progress photos
  const {
    data: progressData,
  } = useQuery({
    queryKey: ['progressPhotos', projectId],
    queryFn: () => getProgressPhotos(projectId, { limit: 10 }),
    enabled: !!projectId && activeTab === 'progress',
  });

  // Fetch project tasks
  const {
    data: tasksData,
  } = useQuery({
    queryKey: ['projectTasks', projectId],
    queryFn: () => listTasks({ projectId }),
    enabled: !!projectId && activeTab === 'tasks',
  });

  // Fetch closure record if project is closed
  const { data: closureData } = useQuery({
    queryKey: ['projectClosure', projectId],
    queryFn: () => getClosure(projectId),
    enabled: !!projectId && projectData?.data?.status === 'closed',
  });

  const project = projectData?.data;
  // getProject() catches query errors and returns them in the payload rather than
  // throwing, so React Query's own `error` stays null. Surface the payload error too
  // (issues #264 / #244): a real query failure (e.g. PGRST201 ambiguous embed) must
  // show as an error, not masquerade as "project not found". PostgREST returns
  // PGRST116 only when a row genuinely does not exist — that alone maps to not-found.
  const loadError = error || projectData?.error;
  const budgetSummary = project?.budgetSummary;
  const expenses = expensesData?.data?.expenses || [];
  const progressPhotos = progressData?.data || [];
  const tasks = tasksData?.data || [];
  const closureRecord = closureData?.data;

  /**
   * Handle edit project
   */
  const handleEdit = () => {
    navigate(`/proyectos/${projectId}/editar`);
  };

  /**
   * Handle inline PO number edit start
   */
  const handleEditPOStart = () => {
    setEditedPONumber(project?.po_number || '');
    setIsEditingPO(true);
  };

  /**
   * Handle inline PO number save
   */
  const handleSavePO = async () => {
    try {
      setActionLoading(true);
      const result = await updateProject(projectId, { po_number: editedPONumber });

      if (result.error) throw result.error;

      toast.success('PO Number actualizado exitosamente');
      setIsEditingPO(false);
      refetch();
      queryClient.invalidateQueries(['projects']);
    } catch (error) {
      console.error('Update PO error:', error);
      toast.error('Error al actualizar el PO Number');
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * Handle inline PO number cancel
   */
  const handleCancelPO = () => {
    setEditedPONumber('');
    setIsEditingPO(false);
  };

  /**
   * Handle inline budget (quoted_amount) save (#403)
   */
  const handleSaveBudget = async () => {
    const amount = parseFloat(editedBudget);
    if (Number.isNaN(amount) || amount < 0) {
      toast.error('Ingresa un monto válido');
      return;
    }

    try {
      setActionLoading(true);
      const result = await updateProject(projectId, { quoted_amount: amount });

      if (result.error) throw result.error;

      toast.success('Presupuesto actualizado exitosamente');
      setIsEditingBudget(false);
      refetch();
      queryClient.invalidateQueries(['projects']);
    } catch (error) {
      console.error('Update budget error:', error);
      toast.error('Error al actualizar el presupuesto');
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * Handle inline budget cancel
   */
  const handleCancelBudget = () => {
    setEditedBudget('');
    setIsEditingBudget(false);
  };

  /**
   * Handle approve quotation from project detail
   */
  const handleApproveQuotation = async (quotationId) => {
    try {
      setQuotationActionLoading(true);
      const result = await approveQuotation(quotationId);

      if (result.error) throw result.error;

      toast.success('Cotización aprobada exitosamente');
      refetch();
      queryClient.invalidateQueries(['quotations']);
    } catch (error) {
      console.error('Approve quotation error:', error);
      toast.error(error.message || 'Error al aprobar la cotización');
    } finally {
      setQuotationActionLoading(false);
    }
  };

  /**
   * Handle reject quotation from project detail
   */
  const handleRejectQuotation = async () => {
    if (!rejectReason.trim() || !rejectTarget) return;

    try {
      setQuotationActionLoading(true);
      const result = await rejectQuotation(rejectTarget, rejectReason);

      if (result.error) throw result.error;

      toast.success('Cotización rechazada');
      setShowRejectModal(false);
      setRejectTarget(null);
      setRejectReason('');
      refetch();
      queryClient.invalidateQueries(['quotations']);
    } catch (error) {
      console.error('Reject quotation error:', error);
      toast.error(error.message || 'Error al rechazar la cotización');
    } finally {
      setQuotationActionLoading(false);
    }
  };

  /**
   * Handle closure wizard completion
   */
  const handleClosureComplete = () => {
    setShowClosureWizard(false);
    refetch();
    queryClient.invalidateQueries(['projects']);
    queryClient.invalidateQueries(['projectClosure', projectId]);
  };

  // Loading state
  if (isLoading) {
    return (
      <DashboardLayout>
        <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Cargando proyecto...</span>
          </Spinner>
        </Container>
      </DashboardLayout>
    );
  }

  // Error state — any real query failure (not a genuinely-missing row) surfaces here
  // instead of silently falling through to "Proyecto no encontrado" (issues #264/#244).
  if (loadError && loadError.code !== 'PGRST116') {
    // El texto crudo de PostgREST ("column projects.foo does not exist") no
    // es copy: nombra objetos internos que el usuario no puede accionar y no
    // le dice qué hacer. El detalle técnico va a la consola, donde sirve.
    // eslint-disable-next-line no-console
    console.error('[ProjectDetail] fallo al cargar el proyecto', loadError);
    return (
      <DashboardLayout>
        <Container className="mt-4">
          <Alert variant="danger" data-testid="project-detail-load-error">
            <Alert.Heading>No se pudo cargar el proyecto</Alert.Heading>
            <p>
              Hubo un problema al consultar el servidor. Revisá tu conexión y volvé a
              intentarlo; si sigue fallando, avisá al administrador.
            </p>
            <div className="d-flex flex-wrap gap-2">
              <Button variant="danger" onClick={() => refetch()} data-testid="project-detail-retry">
                <i className="bi bi-arrow-clockwise me-1" aria-hidden="true"></i>
                Reintentar
              </Button>
              <Button variant="outline-danger" onClick={() => navigate('/proyectos')}>
                Volver a Proyectos
              </Button>
            </div>
          </Alert>
        </Container>
      </DashboardLayout>
    );
  }

  if (!project) {
    return (
      <DashboardLayout>
        <Container className="mt-4">
          <Alert variant="warning">
            <Alert.Heading>Proyecto no encontrado</Alert.Heading>
            <p>El proyecto solicitado no existe o fue eliminado.</p>
            <Button variant="outline-warning" onClick={() => navigate('/proyectos')}>
              Volver a Proyectos
            </Button>
          </Alert>
        </Container>
      </DashboardLayout>
    );
  }

  const statusInfo = formatProjectStatus(project.status);
  const canEdit = isAdmin || isTechnician;
  const canClose = isAdmin && project.status !== PROJECT_STATUS.CLOSED;

  return (
    <DashboardLayout>
      <Container fluid className="py-4">
        {/* Header */}
        <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-start mb-3">
            <div>
              <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
                <h2 className="mb-0">{project.title || project.project_number}</h2>
                <Badge bg={statusInfo.variant}>
                  <i className={`bi ${statusInfo.icon} me-1`}></i>
                  {statusInfo.label}
                </Badge>
                {project.po_number && (
                  <Badge bg="info">PO: {project.po_number}</Badge>
                )}
              </div>
              <div className="mb-2">
                <span
                  className="badge bg-light text-secondary border fw-normal font-monospace"
                  title="ID interno del proyecto (generado automáticamente)"
                >
                  <i className="bi bi-hash me-1"></i>{project.project_number}
                </span>
              </div>
              {project.client && (
                <p className="mb-0">
                  <i className="bi bi-building me-2"></i>
                  <strong>Cliente:</strong> {project.client.company_name || project.client.primary_contact_name}
                </p>
              )}
            </div>
            <div className="d-flex align-items-center gap-2">
              {canEdit && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={handleEdit}
                  data-testid="project-edit-button"
                >
                  <i className="bi bi-pencil me-1"></i>
                  Editar Proyecto
                </Button>
              )}
            {(canEdit || isAdmin || canClose) && (
              <KebabMenu
                label={`Acciones de ${project.title || project.project_number}`}
                iconClassName="fs-5"
              >
                {canEdit && (
                  <Dropdown.Item onClick={handleEdit}>
                    <i className="bi bi-pencil me-2"></i>Editar
                  </Dropdown.Item>
                )}
                {isAdmin && (
                  <Dropdown.Item onClick={handleEditPOStart}>
                    <i className="bi bi-file-text me-2"></i>{project.po_number ? 'Actualizar PO' : 'Agregar PO'}
                  </Dropdown.Item>
                )}
                {canClose && (
                  <>
                    <Dropdown.Divider />
                    <Dropdown.Item className="text-warning" onClick={() => setShowClosureWizard(true)}>
                      <i className="bi bi-lock me-2"></i>Cerrar Proyecto
                    </Dropdown.Item>
                  </>
                )}
              </KebabMenu>
            )}
            </div>
          </div>

          {/* Budget Alert Banner — internal only (#500). The banner spells out
              the quoted AND the spent amount, i.e. the margin, in a red box a
              client cannot miss. An overrun is ProMedic's problem to manage,
              not news to break to the customer. */}
          {canViewInternalCosts && budgetSummary && budgetSummary.variancePercentage >= BUDGET_WARNING_THRESHOLD && (
            <BudgetAlertBanner
              variancePercentage={budgetSummary.variancePercentage}
              quotedAmount={budgetSummary.quotedAmount}
              spentAmount={budgetSummary.totalExpenses}
              className="mb-3"
            />
          )}
        </Col>
      </Row>

      {/* Tabs */}
      <Row>
        <Col>
          {/* 10 pestañas con ícono + etiqueta: sin el scroll horizontal de
              ProjectDetail.css, Bootstrap las envuelve en ~5 filas a 375px
              (~250px de cromo antes de cualquier dato del proyecto). */}
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="mb-3 project-detail-tabs"
          >
            {/* Overview Tab */}
            <Tab eventKey="overview" title={<><i className="bi bi-info-circle me-2"></i>Resumen</>}>
              <Row>
                {/* #518: with both money gates closed (the technician) every
                    row inside this card is suppressed, so the card itself
                    would render as a titled empty box. Drop the whole panel
                    rather than leave a "Resumen Presupuestario" heading over
                    nothing. The client still has `canViewQuotedAmount`, so
                    their single-row version survives. */}
                {(canViewQuotedAmount || canViewInternalCosts) && (
                <Col lg={6} className="mb-4">
                  <Card>
                    <Card.Header className="d-flex justify-content-between align-items-center">
                      <h5 className="mb-0">
                        <i className="bi bi-cash-stack me-2"></i>
                        Resumen Presupuestario
                      </h5>
                      {canEditBudget && !isEditingBudget && (
                        <Button
                          variant="link"
                          size="sm"
                          className="p-0"
                          onClick={() => {
                            setEditedBudget(budgetSummary?.quotedAmount ?? '');
                            setIsEditingBudget(true);
                          }}
                          data-testid="project-budget-edit-button"
                          title="Asignar/editar presupuesto"
                        >
                          <i className="bi bi-pencil-square"></i>
                        </Button>
                      )}
                    </Card.Header>
                    <Card.Body>
                      {/* #727: el lápiz de acá es el camino por el que el
                          administrador carga el monto provisional, y hasta
                          ahora no decía nada. El campo equivalente de *Editar
                          Proyecto* prometía lo contrario de lo que hace la base
                          (que aprobar una cotización lo sobrescribe); el
                          trigger `update_project_quoted_amount()` escribe
                          `WHERE ... AND quoted_amount IS NULL` desde #639, así
                          que asignar acá BLOQUEA que la primera cotización
                          aprobada fije la línea base, en silencio. */}
                      {isEditingBudget && (
                        <div className="d-flex gap-2 align-items-center mb-3">
                          <Form.Control
                            type="number"
                            min="0"
                            step="0.01"
                            value={editedBudget}
                            onChange={(e) => setEditedBudget(e.target.value)}
                            placeholder="0.00"
                            size="sm"
                            data-testid="project-budget-input"
                            disabled={actionLoading}
                          />
                          <Button
                            variant="success"
                            size="sm"
                            onClick={handleSaveBudget}
                            disabled={actionLoading || editedBudget === ''}
                            data-testid="project-budget-save"
                          >
                            <i className="bi bi-check-lg"></i>
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={handleCancelBudget}
                            disabled={actionLoading}
                            data-testid="project-budget-cancel"
                          >
                            <i className="bi bi-x-lg"></i>
                          </Button>
                        </div>
                      )}
                      {isEditingBudget && (
                        <div
                          className="text-muted small mb-3"
                          data-testid="project-budget-baseline-note"
                        >
                          Este monto queda como la línea base de la obra:
                          aprobar una cotización más adelante NO lo reemplaza
                          (#639). Si querés que el monto lo fije la cotización,
                          dejá el presupuesto sin asignar hasta aprobarla.
                        </div>
                      )}
                      {budgetSummary ? (
                        <div data-testid="project-budget-summary">
                          {/* Monto Cotizado — its OWN gate (#518), not the
                              internal-cost one. The client is entitled to it
                              (it is the price they agreed to pay) and the
                              technician is not (what we charge the client is
                              not theirs to see). No single flag expresses
                              both, which is why there are two. */}
                          {canViewQuotedAmount && (
                            <div className="d-flex justify-content-between mb-3">
                              <span className="text-muted">Monto Cotizado:</span>
                              <strong className="text-primary" data-testid="project-budget-quoted">
                                {formatCurrency(budgetSummary.quotedAmount)}
                              </strong>
                            </div>
                          )}
                          {canViewInternalCosts && (
                            <>
                              <div className="d-flex justify-content-between mb-3">
                                <span className="text-muted">Total Gastado:</span>
                                <strong data-testid="project-budget-spent">
                                  {formatCurrency(budgetSummary.totalExpenses)}
                                </strong>
                              </div>
                              <div className="d-flex justify-content-between mb-3">
                                <span className="text-muted">Disponible:</span>
                                <strong
                                  // #685: sin presupuesto `variance` viene null
                                  // y se muestra "-" en gris; el verde era un
                                  // "todo bien" sobre una cifra inexistente.
                                  className={budgetSummary.variance == null ? 'text-muted' : budgetSummary.variance < 0 ? 'text-danger' : 'text-success'}
                                  data-testid="project-budget-available"
                                >
                                  {formatCurrency(budgetSummary.variance)}
                                </strong>
                              </div>
                              <div className="d-flex justify-content-between">
                                <span className="text-muted">Uso del Presupuesto:</span>
                                <strong
                                  className={
                                    budgetSummary.variancePercentage == null ? 'text-muted' :
                                    budgetSummary.variancePercentage >= BUDGET_CRITICAL_THRESHOLD ? 'text-danger' :
                                    budgetSummary.variancePercentage >= BUDGET_WARNING_THRESHOLD ? 'text-warning' :
                                    'text-success'
                                  }
                                  data-testid="project-budget-usage"
                                >
                                  {formatPercentage(budgetSummary.variancePercentage, 1)}
                                </strong>
                              </div>
                            </>
                          )}
                        </div>
                      ) : (
                        <p className="text-muted mb-0">
                          No hay información presupuestaria disponible.
                          <br />
                          Crea una cotización para comenzar a rastrear el presupuesto.
                        </p>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
                )}

                <Col lg={6} className="mb-4">
                  <Card>
                    <Card.Header>
                      <h5 className="mb-0">
                        <i className="bi bi-info-square me-2"></i>
                        Información del Proyecto
                      </h5>
                    </Card.Header>
                    <Card.Body>
                      {/* PO Number - Inline Editable (Admin Only) */}
                      <div className="mb-3">
                        <small className="text-muted d-block">PO Number:</small>
                        {isEditingPO ? (
                          <div className="d-flex gap-2 align-items-center">
                            <Form.Control
                              type="text"
                              value={editedPONumber}
                              onChange={(e) => setEditedPONumber(e.target.value)}
                              placeholder="PO-XXXX"
                              size="sm"
                              data-testid="project-po-number-input"
                              disabled={actionLoading}
                            />
                            <Button
                              variant="success"
                              size="sm"
                              onClick={handleSavePO}
                              disabled={actionLoading || !editedPONumber.trim()}
                              data-testid="project-po-number-save"
                            >
                              <i className="bi bi-check-lg"></i>
                            </Button>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={handleCancelPO}
                              disabled={actionLoading}
                              data-testid="project-po-number-cancel"
                            >
                              <i className="bi bi-x-lg"></i>
                            </Button>
                          </div>
                        ) : (
                          <div className="d-flex gap-2 align-items-center">
                            <strong data-testid="project-po-number-display">
                              {project.po_number || 'No asignado'}
                            </strong>
                            {isAdmin && (
                              <Button
                                variant="link"
                                size="sm"
                                className="p-0"
                                onClick={handleEditPOStart}
                                data-testid="project-po-number-edit-button"
                              >
                                <i className="bi bi-pencil"></i>
                              </Button>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="mb-3">
                        <small className="text-muted d-block">Descripción:</small>
                        <p>{project.description || 'Sin descripción'}</p>
                      </div>
                      {project.estimated_payment_date && (
                        <div className="mb-3">
                          <small className="text-muted d-block">Fecha Estimada de Pago:</small>
                          <strong>{formatDate(project.estimated_payment_date)}</strong>
                        </div>
                      )}
                      <div className="mb-2">
                        <small className="text-muted d-block">Fecha de Creación:</small>
                        <strong>{formatDate(project.created_at)}</strong>
                      </div>
                      {project.created_by_profile && (
                        <div>
                          <small className="text-muted d-block">Creado Por:</small>
                          <strong>{project.created_by_profile.full_name}</strong>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Closure Info (shown when project is closed) */}
              {project.status === PROJECT_STATUS.CLOSED && closureRecord && (
                <Row className="mb-4">
                  <Col>
                    <Card border="dark">
                      <Card.Header className="bg-dark text-white">
                        <h5 className="mb-0">
                          <i className="bi bi-lock me-2"></i>
                          Informacion de Cierre
                        </h5>
                      </Card.Header>
                      <Card.Body>
                        <div className="d-flex flex-wrap gap-4">
                          <div>
                            <small className="text-muted d-block">Fecha de Cierre:</small>
                            <strong>{formatDate(closureRecord.closure_date)}</strong>
                          </div>
                          {closureRecord.closed_by_profile && (
                            <div>
                              <small className="text-muted d-block">Cerrado Por:</small>
                              <strong>{closureRecord.closed_by_profile.full_name}</strong>
                            </div>
                          )}
                          {closureRecord.customer_rating && (
                            <div>
                              <small className="text-muted d-block">Calificacion:</small>
                              <div>
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <i
                                    key={star}
                                    className={`bi ${star <= closureRecord.customer_rating ? 'bi-star-fill text-warning' : 'bi-star text-muted'}`}
                                    style={{ marginRight: '2px' }}
                                  ></i>
                                ))}
                              </div>
                            </div>
                          )}
                          {/* #500: final_variance is quoted - spent at closure,
                              i.e. the margin ProMedic booked on this project.
                              Same gate as the budget panel above. */}
                          {canViewInternalCosts && (
                            <div>
                              <small className="text-muted d-block">Variacion Final:</small>
                              <strong
                                className={closureRecord.final_variance >= 0 ? 'text-success' : 'text-danger'}
                                data-testid="project-closure-final-variance"
                              >
                                {formatCurrency(closureRecord.final_variance)}
                              </strong>
                            </div>
                          )}
                        </div>
                        {closureRecord.completion_summary && (
                          <div className="mt-3">
                            <small className="text-muted d-block">Resumen:</small>
                            <p className="mb-0">{closureRecord.completion_summary}</p>
                          </div>
                        )}
                        {closureRecord.customer_feedback && (
                          <div className="mt-2">
                            <small className="text-muted d-block">Retroalimentacion del Cliente:</small>
                            <p className="mb-0 fst-italic">"{closureRecord.customer_feedback}"</p>
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              )}

              <Row>
                <Col>
                  <Card>
                    <Card.Header className="d-flex justify-content-between align-items-center">
                      <h5 className="mb-0">
                        <i className="bi bi-clock-history me-2"></i>
                        Actividad Reciente
                      </h5>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => setActiveTab('bitacora')}
                      >
                        Ver Bitacora Completa
                        <i className="bi bi-arrow-right ms-1"></i>
                      </Button>
                    </Card.Header>
                    <Card.Body>
                      <ProjectTimeline
                        projectId={projectId}
                        project={project}
                      />
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Tab>

            {/* Quotations Tab */}
            <Tab eventKey="quotations" title={<><i className="bi bi-file-text me-2"></i>Cotizaciones</>}>
              <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Cotizaciones del Proyecto</h5>
                  {canEdit && (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => navigate(`/cotizaciones/nueva?projectId=${projectId}`)}
                    >
                      <i className="bi bi-plus-circle me-1"></i>
                      Nueva Cotización
                    </Button>
                  )}
                </Card.Header>
                <Card.Body>
                  {project.quotations && project.quotations.length > 0 ? (
                    <>
                      {/* Desktop Table View */}
                      <div className="d-none d-md-block">
                        <Table hover responsive>
                          <thead>
                            <tr>
                              <th>Número</th>
                              <th>Estado</th>
                              <th className="text-end">Total</th>
                              <th>Fecha</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {project.quotations.map((quotation) => (
                              <tr key={quotation.id}>
                                <td>{quotation.quotation_number}</td>
                                <td>
                                  {(() => {
                                    const qStatus = formatQuotationStatus(quotation.status);
                                    return (
                                      <Badge bg={qStatus.variant}>{qStatus.label}</Badge>
                                    );
                                  })()}
                                </td>
                                <td className="text-end">{formatCurrency(quotation.total)}</td>
                                <td>{formatDate(quotation.created_at)}</td>
                                <td className="text-end">
                                  <KebabMenu label={`Acciones de la cotización ${quotation.quotation_number}`}>
                                    <Dropdown.Item onClick={() => navigate(`/cotizaciones/${quotation.id}`)}>
                                      <i className="bi bi-eye me-2"></i>Ver
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => navigate(`/cotizaciones/${quotation.id}/editar`)}>
                                      <i className="bi bi-pencil me-2"></i>Editar
                                    </Dropdown.Item>
                                    {isAdmin && quotation.status === QUOTATION_STATUS.UNDER_REVIEW && (
                                      <>
                                        <Dropdown.Divider />
                                        <Dropdown.Item
                                          onClick={() => handleApproveQuotation(quotation.id)}
                                          disabled={quotationActionLoading}
                                        >
                                          <i className="bi bi-check-circle me-2 text-success"></i>Aprobar
                                        </Dropdown.Item>
                                        <Dropdown.Item
                                          onClick={() => { setRejectTarget(quotation.id); setShowRejectModal(true); }}
                                        >
                                          <i className="bi bi-x-circle me-2 text-danger"></i>Rechazar
                                        </Dropdown.Item>
                                      </>
                                    )}
                                  </KebabMenu>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>

                      {/* Mobile Cards View */}
                      <div className="d-md-none">
                        {project.quotations.map((quotation) => (
                          <Card key={quotation.id} className="mb-3 shadow-sm">
                            <Card.Body className="p-3">
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <div>
                                  <h6 className="mb-1 fw-bold">{quotation.quotation_number}</h6>
                                  {(() => {
                                    const qStatus = formatQuotationStatus(quotation.status);
                                    return (
                                      <Badge bg={qStatus.variant}>{qStatus.label}</Badge>
                                    );
                                  })()}
                                </div>
                                <KebabMenu label={`Acciones de la cotización ${quotation.quotation_number}`}>
                                  <Dropdown.Item onClick={() => navigate(`/cotizaciones/${quotation.id}`)}>
                                    <i className="bi bi-eye me-2"></i>Ver
                                  </Dropdown.Item>
                                  <Dropdown.Item onClick={() => navigate(`/cotizaciones/${quotation.id}/editar`)}>
                                    <i className="bi bi-pencil me-2"></i>Editar
                                  </Dropdown.Item>
                                  {isAdmin && quotation.status === QUOTATION_STATUS.UNDER_REVIEW && (
                                    <>
                                      <Dropdown.Divider />
                                      <Dropdown.Item
                                        onClick={() => handleApproveQuotation(quotation.id)}
                                        disabled={quotationActionLoading}
                                      >
                                        <i className="bi bi-check-circle me-2 text-success"></i>Aprobar
                                      </Dropdown.Item>
                                      <Dropdown.Item
                                        onClick={() => { setRejectTarget(quotation.id); setShowRejectModal(true); }}
                                      >
                                        <i className="bi bi-x-circle me-2 text-danger"></i>Rechazar
                                      </Dropdown.Item>
                                    </>
                                  )}
                                </KebabMenu>
                              </div>
                              <div className="d-flex justify-content-between align-items-center mt-3">
                                <small className="text-muted">
                                  <i className="bi bi-calendar me-1"></i>
                                  {formatDate(quotation.created_at)}
                                </small>
                                <span className="text-primary fw-bold fs-5">
                                  {formatCurrency(quotation.total)}
                                </span>
                              </div>
                            </Card.Body>
                          </Card>
                        ))}
                      </div>
                    </>
                  ) : (
                    <p className="text-muted text-center py-4">
                      No hay cotizaciones para este proyecto.
                    </p>
                  )}
                </Card.Body>
              </Card>
            </Tab>

            {/* Materiales — validación de inventario, READ-ONLY (paridad con
                `check_materials_availability`, que hasta ahora sólo existía
                para el agente). Va pegada a Cotizaciones porque de ahí sale la
                lista: los materiales que se le cotizaron al cliente. */}
            {canValidateMaterials && (
            <Tab eventKey="materials" title={<><i className="bi bi-box-seam me-2"></i>Materiales</>}>
              <ProjectMaterialsTab
                projectId={projectId}
                active={activeTab === 'materials'}
              />
            </Tab>
            )}

            {/* Milestones Tab */}
            <Tab eventKey="milestones" title={<><i className="bi bi-flag me-2"></i>Hitos</>}>
              <MilestoneList projectId={projectId} canEdit={canEdit} />
            </Tab>

            {/* Invoices Tab */}
            <Tab eventKey="invoices" title={<><i className="bi bi-receipt-cutoff me-2"></i>Facturas</>}>
              <InvoiceList projectId={projectId} quotations={project.quotations || []} />
            </Tab>

            {/* Configuración del proyecto — hoy solo la delegación de
                aprobación de compras que pidió Jorge ("en configuración por
                proyecto el admin puede asignar ese permiso a un líder").
                Se muestra a staff interno: el admin la edita, el resto la ve.
                No se le ofrece al cliente — quién aprueba las compras de
                ProMedic es información interna, igual que Gastos (#500). */}
            {canViewInternalCosts && (
            <Tab eventKey="configuracion" title={<><i className="bi bi-gear me-2"></i>Configuración</>}>
              <ProjectLeadersPanel projectId={projectId} canManage={isAdmin} />
            </Tab>
            )}

            {/* Expenses Tab — internal only (#500). Every row here is a
                supplier name plus what ProMedic paid them; the client adding
                the column up gets the cost side of their own quote. */}
            {canViewInternalCosts && (
            <Tab eventKey="expenses" title={<><i className="bi bi-receipt me-2"></i>Gastos</>}>
              <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Gastos del Proyecto</h5>
                  {canEdit && (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => navigate(`/gastos/nuevo?projectId=${projectId}`)}
                    >
                      <i className="bi bi-plus-circle me-1"></i>
                      Registrar Gasto
                    </Button>
                  )}
                </Card.Header>
                <Card.Body>
                  {expenses.length > 0 ? (
                    <>
                      {/* Desktop Table View */}
                      <div className="d-none d-md-block">
                        <Table hover responsive>
                          <thead>
                            <tr>
                              <th>Fecha</th>
                              <th>Categoría</th>
                              <th>Proveedor</th>
                              <th className="text-end">Monto</th>
                              <th>Recibo</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {expenses.slice(0, 10).map((expense) => (
                              <tr key={expense.id}>
                                <td>{formatDate(expense.expense_date)}</td>
                                <td>{expense.category}</td>
                                <td>{expense.vendor || '-'}</td>
                                <td className="text-end">{formatCurrency(expense.amount)}</td>
                                <td>
                                  {expense.receipt_url ? (
                                    <a
                                      href={expense.receipt_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      aria-label={`Ver recibo del gasto de ${expense.vendor || 'proveedor sin nombre'} del ${formatDate(expense.expense_date)}`}
                                    >
                                      <i className="bi bi-file-earmark-pdf" aria-hidden="true"></i>
                                    </a>
                                  ) : (
                                    '-'
                                  )}
                                </td>
                                <td className="text-end">
                                  <KebabMenu
                                    label={`Acciones del gasto de ${expense.vendor || 'proveedor sin nombre'} del ${formatDate(expense.expense_date)}`}
                                  >
                                    <Dropdown.Item onClick={() => navigate(`/gastos/${expense.id}`)}>
                                      <i className="bi bi-eye me-2"></i>Ver
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => navigate(`/gastos/${expense.id}/editar`)}>
                                      <i className="bi bi-pencil me-2"></i>Editar
                                    </Dropdown.Item>
                                  </KebabMenu>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>

                      {/* Mobile Cards View */}
                      <div className="d-md-none">
                        {expenses.slice(0, 10).map((expense) => (
                          <Card key={expense.id} className="mb-3 shadow-sm">
                            <Card.Body className="p-3">
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <div>
                                  <Badge bg="secondary" className="mb-1">{expense.category}</Badge>
                                  <p className="mb-0 small text-muted">{expense.vendor || 'Sin proveedor'}</p>
                                </div>
                                <KebabMenu
                                  label={`Acciones del gasto de ${expense.vendor || 'proveedor sin nombre'} del ${formatDate(expense.expense_date)}`}
                                >
                                  <Dropdown.Item onClick={() => navigate(`/gastos/${expense.id}`)}>
                                    <i className="bi bi-eye me-2"></i>Ver
                                  </Dropdown.Item>
                                  <Dropdown.Item onClick={() => navigate(`/gastos/${expense.id}/editar`)}>
                                    <i className="bi bi-pencil me-2"></i>Editar
                                  </Dropdown.Item>
                                  {expense.receipt_url && (
                                    <Dropdown.Item href={expense.receipt_url} target="_blank" rel="noopener noreferrer">
                                      <i className="bi bi-file-earmark-pdf me-2"></i>Recibo
                                    </Dropdown.Item>
                                  )}
                                </KebabMenu>
                              </div>
                              <div className="d-flex justify-content-between align-items-center mt-2">
                                <small className="text-muted">
                                  <i className="bi bi-calendar me-1"></i>
                                  {formatDate(expense.expense_date)}
                                </small>
                                <span className="text-danger fw-bold fs-5">
                                  {formatCurrency(expense.amount)}
                                </span>
                              </div>
                            </Card.Body>
                          </Card>
                        ))}
                      </div>
                    </>
                  ) : (
                    <p className="text-muted text-center py-4">
                      No hay gastos registrados para este proyecto.
                    </p>
                  )}
                </Card.Body>
              </Card>
            </Tab>
            )}

            {/* Tasks Tab */}
            <Tab eventKey="tasks" title={<><i className="bi bi-list-check me-2"></i>Tareas</>}>
              <Card>
                <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <h5 className="mb-0">Tareas del Proyecto</h5>
                  <div className="d-flex gap-2">
                    {/* #459: create a task pre-scoped to this project — TaskForm
                        already reads ?projectId= and preselects it. */}
                    {canEdit && (
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => navigate(`/tareas/nueva?projectId=${projectId}`)}
                        data-testid="project-detail-new-task-button"
                      >
                        <i className="bi bi-plus-lg me-1"></i>
                        Nueva Tarea
                      </Button>
                    )}
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => navigate(`/tareas/list?projectId=${projectId}`)}
                    >
                      Ver todas en Tareas
                      <i className="bi bi-arrow-right ms-1"></i>
                    </Button>
                  </div>
                </Card.Header>
                <Card.Body>
                  {tasks.length > 0 ? (
                    <>
                      {/* Desktop Table View */}
                      <div className="d-none d-md-block">
                        <Table hover responsive>
                          <thead>
                            <tr>
                              <th>Tarea</th>
                              <th>Estado</th>
                              <th>Prioridad</th>
                              <th>Asignado a</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {tasks.map((task) => (
                              <tr key={task.id}>
                                <td>
                                  <div>
                                    <strong>{task.task_number}</strong>
                                    <br />
                                    <small className="text-muted">{task.title}</small>
                                  </div>
                                </td>
                                <td><TaskStatusBadge status={task.status} /></td>
                                <td><TaskPriorityBadge priority={task.priority} /></td>
                                <td>
                                  {task.assignees && task.assignees.length > 0
                                    ? task.assignees.map(a => a.assigned_user?.full_name).filter(Boolean).join(', ')
                                    : <span className="text-muted">Sin asignar</span>
                                  }
                                </td>
                                <td className="text-end">
                                  <KebabMenu label={`Acciones de la tarea ${task.task_number}`}>
                                    <Dropdown.Item onClick={() => navigate(`/tareas/${task.id}`)}>
                                      <i className="bi bi-eye me-2"></i>Ver
                                    </Dropdown.Item>
                                  </KebabMenu>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>

                      {/* Mobile Cards View */}
                      <div className="d-md-none">
                        {tasks.map((task) => (
                          <Card key={task.id} className="mb-3 shadow-sm">
                            <Card.Body className="p-3">
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <div>
                                  <h6 className="mb-1 fw-bold">{task.task_number}</h6>
                                  <p className="mb-1 small">{task.title}</p>
                                  <div className="d-flex gap-1">
                                    <TaskStatusBadge status={task.status} />
                                    <TaskPriorityBadge priority={task.priority} />
                                  </div>
                                </div>
                                <KebabMenu label={`Acciones de la tarea ${task.task_number}`}>
                                  <Dropdown.Item onClick={() => navigate(`/tareas/${task.id}`)}>
                                    <i className="bi bi-eye me-2"></i>Ver
                                  </Dropdown.Item>
                                </KebabMenu>
                              </div>
                              {task.assignees && task.assignees.length > 0 && (
                                <small className="text-muted">
                                  <i className="bi bi-person me-1"></i>
                                  {task.assignees.map(a => a.assigned_user?.full_name).filter(Boolean).join(', ')}
                                </small>
                              )}
                            </Card.Body>
                          </Card>
                        ))}
                      </div>
                    </>
                  ) : (
                    <p className="text-muted text-center py-4">
                      No hay tareas para este proyecto.
                    </p>
                  )}
                </Card.Body>
              </Card>
            </Tab>

            {/* Progress Tab */}
            <Tab eventKey="progress" title={<><i className="bi bi-images me-2"></i>Fotos</>}>
              <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Fotos de Progreso</h5>
                  {canEdit && (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => navigate(`/proyectos/${projectId}/progreso/subir`)}
                    >
                      <i className="bi bi-upload me-1"></i>
                      Subir Fotos
                    </Button>
                  )}
                </Card.Header>
                <Card.Body>
                  {progressPhotos.length > 0 ? (
                    <Row>
                      {progressPhotos.map((photo) => (
                        <Col md={4} lg={3} key={photo.id} className="mb-3">
                          <Card
                            role="button"
                            tabIndex={0}
                            className="progress-photo-card h-100"
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                              setHighlightedEventId(photo.id);
                              setActiveTab('bitacora');
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setHighlightedEventId(photo.id);
                                setActiveTab('bitacora');
                              }
                            }}
                            title="Ver esta foto en la bitacora"
                          >
                            <Card.Img
                              variant="top"
                              src={photo.photo_url}
                              alt={photo.caption || 'Progress photo'}
                              style={{ height: '200px', objectFit: 'cover' }}
                            />
                            <Card.Body className="p-2">
                              <small className="text-muted d-block">
                                {formatDate(photo.uploaded_at)}
                              </small>
                              {photo.caption && (
                                <small>{photo.caption}</small>
                              )}
                            </Card.Body>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  ) : (
                    <p className="text-muted text-center py-4">
                      No hay fotos de progreso para este proyecto.
                    </p>
                  )}
                </Card.Body>
              </Card>
            </Tab>

            {/* Bitacora Tab */}
            <Tab eventKey="bitacora" title={<><i className="bi bi-clock-history me-2"></i>Bitacora</>}>
              <Card>
                <Card.Body>
                  <ProjectTimeline
                    projectId={projectId}
                    project={project}
                    highlightedEventId={activeTab === 'bitacora' ? highlightedEventId : null}
                    onHighlightConsumed={() => setHighlightedEventId(null)}
                  />
                </Card.Body>
              </Card>
            </Tab>

            {/* Files Tab */}
            <Tab eventKey="files" title={<><i className="bi bi-folder me-2"></i>Archivos</>}>
              <Card>
                <Card.Header>
                  <h5 className="mb-0">Archivos del Proyecto</h5>
                </Card.Header>
                <Card.Body>
                  <p className="text-muted text-center py-4">
                    <i className="bi bi-info-circle me-2"></i>
                    Función de gestión de archivos en desarrollo
                  </p>
                </Card.Body>
              </Card>
            </Tab>
          </Tabs>
        </Col>
      </Row>

      {/* Quotation Rejection Modal */}
      <Modal
        show={showRejectModal}
        onHide={() => { setShowRejectModal(false); setRejectTarget(null); setRejectReason(''); }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">
            <i className="bi bi-x-circle me-2"></i>
            Rechazar Cotización
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            <i className="bi bi-exclamation-triangle me-2"></i>
            La cotización será marcada como rechazada y el creador será notificado.
          </Alert>
          <Form.Group>
            <Form.Label>
              Razón del Rechazo <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Explica por qué se rechaza esta cotización..."
              autoFocus
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => { setShowRejectModal(false); setRejectTarget(null); setRejectReason(''); }}
            disabled={quotationActionLoading}
          >
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={handleRejectQuotation}
            disabled={quotationActionLoading || !rejectReason.trim()}
          >
            {quotationActionLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Rechazando...
              </>
            ) : (
              <>
                <i className="bi bi-x-circle me-1"></i>
                Confirmar Rechazo
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Project Closure Wizard */}
      <ProjectClosureWizard
        show={showClosureWizard}
        onHide={() => setShowClosureWizard(false)}
        project={project}
        budgetSummary={budgetSummary}
        onClosureComplete={handleClosureComplete}
      />
      </Container>
    </DashboardLayout>
  );
};

export default ProjectDetail;
